// Components
import * as React from 'react';

// Misc
import { PubListConfigInternal } from '../types';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

/** @jsx jsx */
import { jsx, Container, Button, Flex, Text } from 'theme-ui';
import { ViewMessageWrapper, Preview, PreviewRUB } from './theme';
import { Divider } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { OAuth2Gate, OAuth2Sender } from '../../Auth/components';
import { Prompt } from 'react-router-dom';

import { ThemeContext } from 'BaseLayout/components';
import {
  PubListConfig,
  PubListConfigListTypeEnum,
} from 'api/publist/models';
import { LoadingSpinner } from 'BaseLayout/components/misc/Spinner';
import SidebarTabs from './SidebarTabs/SidebarTabs';
import { ListTypeSidebarTabMap, SidebarTabListTypeMap } from './SidebarTabs/data';
import { SidebarTab } from './SidebarTabs/types';
import { PublicationData } from './PubListGeneratorBase';

export interface Props {
  assetsAvailable: boolean;
  preview?: string;
  config: PubListConfigInternal;
  viewMessage?: string;
  loading?: boolean;
  onSuccess?: () => void;
  loadPreview: (config: PubListConfig) => any;
  storeConfig: (config: PubListConfig) => Promise<number> | null;
  storeNewConfig: (config: PubListConfig) => Promise<number>;
  updateConfig: (config: PubListConfigInternal) => any;
  alertShow: (message: string) => void;
  clearAllInput: (listType: PubListConfigListTypeEnum) => void;
}

/**
 * Backend is using a slightly different PublistConfig, e.g. person Ids are stored as a number
 * while frontend is using a ReferencedPersonDTO to display furhter information such as names.
 * Therefore some fields of a PublistConfig must be mapped before sending to the backend.
 *
 * @param config The internal (frontend) PublistConfig
 *
 * @returns The external (backend) PublistConfig
 */

export interface CategoryToggleConfig {
  determination_type: boolean;
  citation: boolean;
  group_content: boolean;
  person_filter_func: boolean;
  general_filter: boolean;
  publication_highlight: boolean;
  replace_text_links: boolean;
}

export const PubListGeneratorDesktop: React.FC<Props> = ({
  config,
  alertShow,
  preview,
  updateConfig,
  loadPreview,
  storeConfig,
  storeNewConfig,
  assetsAvailable,
  loading,
  onSuccess,
  viewMessage,
  clearAllInput
}) => {

  window.onbeforeunload = (e) => {
    if(changed) {
      e.preventDefault();
      e.returnValue = '';
    }
  };

  useEffect(() => {
    return () => {
      window.onbeforeunload = null;
    };
  });

  const themeName = React.useContext(ThemeContext);

  const intl: IntlShape = useIntl();

  const [changed, setChanged] = useState<boolean>(false);

  preview = preview?.replaceAll('>HB</a>', '>' + intl.formatMessage({ id: 'publist.preview.links.hb' }) + '</a>')
    .replaceAll('>WoS</a>', '>' + intl.formatMessage({ id: 'publist.preview.links.wos' }) + '</a>')
    .replaceAll('>PubMed</a>', '>' + intl.formatMessage({ id: 'publist.preview.links.pubmed' }) + '</a>')
    .replaceAll('>Scopus</a>', '>' + intl.formatMessage({ id: 'publist.preview.links.scopus' }) + '</a>')
    .replaceAll('>FullText</a>', '>' + intl.formatMessage({ id: 'publist.preview.links.fulltext' }) + '</a>');

  return (
    <>
      {changed && <Prompt message={(location) => {
        return location.hash === '' ? intl.formatMessage({id: 'publist.popup.changed_lost_on_route_change'}) : true;
      }}
      when={changed}></Prompt>}
      <LoadingSpinner active={!assetsAvailable} onSuccess={onSuccess} loading={loading}>
        <div sx={{pb: 10}}>
          <OAuth2Gate anyRoles={['ROLE_GUEST', 'ROLE_USER']}>
            <Container variant="bannerPublist" sx={{
              height: ['7em', '7em', '7em', '7em', '10em', '10em'],
              'h1, h2': {
                fontSize: ['22px', '22px', '22px', '22px', '24px', '24px']
              },
            }}>
              <h1><FormattedMessage id="publist.banner.hochschulbibliographie" /></h1>
              <h2><FormattedMessage id="publist.banner.publistgenerator" /></h2>
            </Container>
          </OAuth2Gate>
          <OAuth2Gate anyRoles={['ROLE_EDITOR', 'ROLE_ADMIN']}>
            <Divider />
          </OAuth2Gate>
          <ViewMessageWrapper>
            {viewMessage}
          </ViewMessageWrapper>

          {/* Sidebar */}
          <Flex>
            <Flex sx={{
              flexDirection: "column",
              maxWidth: ["21em", "21em", "21em", "21em", "25em", "25em"],
              minWidth: ["21em", "21em", "21em", "21em", "25em", "25em"],
              p: 4,
              borderRightWidth: "1px",
              borderRightStyle: "solid",
              borderRightColor: "light",
              fontSize: ['15px', '15px', '15px', '15px', '16px', '16px']
            }}>
              {/* Tabs: publication list | research data list */}
              <SidebarTabs
                initialActiveTab={ListTypeSidebarTabMap[config.listType]}
                onChangeTab={(tab: SidebarTab) => {
                  clearAllInput(SidebarTabListTypeMap[tab]);
                  setChanged(false);
                }}
                showChangeTabConfirmDialog={changed}
              />

              <PublicationData
                config={config}
                alertShow={alertShow}
                preview={preview}
                updateConfig={updateConfig}
                loadPreview={loadPreview}
                storeConfig={storeConfig}
                storeNewConfig={storeNewConfig}
                assetsAvailable={assetsAvailable}
                loading={loading}
                onSuccess={onSuccess}
                viewMessage={viewMessage}
                clearAllInput={clearAllInput}
                catName={"publig.sidebar.tab.publist"}
                configChanged={(e) => setChanged(e)}
                mobileView={false}
              />
            </Flex>
            {/* Content/Preview section */}
            <div className="content">
              <OAuth2Gate isAnonymous>
                <Text sx={{
                  color: "primary",
                  fontWeight: "bold",
                  m: 5,
                  fontSize: ['15px', '15px', '15px', '15px', '16px', '16px']
                }}>
                  <FormattedMessage id="publist.message.logininfo" /> <br />
                  <OAuth2Sender
                    render={({ authUrl }) => (
                      <Button mt={5} onClick={()=>{
                        window.location.href = authUrl;
                      }}><FormattedMessage id="header.login" /></Button>
                    )}
                  />
                </Text>
              </OAuth2Gate>

              {assetsAvailable ? (
                <React.Fragment>
                  {preview && (themeName !== 'rub') && <Preview content={preview} ></Preview>}
                  {preview && (themeName === 'rub') && <PreviewRUB content={preview} ></PreviewRUB>}
                </React.Fragment>
              ) : (
                <></>
              )}
               </div>
          </Flex>
        </div>
      </LoadingSpinner>
    </>
  );
};

export default PubListGeneratorDesktop;
